// DEM-08 (multi-provider v5): slider 6 níveis canônicos de reasoning effort.
// Níveis não suportados pelo modelo selecionado ficam greyed-out com tooltip.
import type { CanonicalEffort } from '@/lib/api';
import { cn } from '@/lib/utils';

const ALL_LEVELS: CanonicalEffort[] = ['minimal', 'low', 'medium', 'high', 'xhigh', 'max'];

interface ReasoningSliderProps {
  value: CanonicalEffort | undefined;
  onChange: (effort: CanonicalEffort) => void;
  /** Lista de níveis suportados pelo modelo selecionado. Outros ficam greyed. */
  supportedLevels: CanonicalEffort[];
  label?: string;
  defaultLevel?: CanonicalEffort;
}

export function ReasoningSlider({
  value,
  onChange,
  supportedLevels,
  label = 'Reasoning effort',
  defaultLevel,
}: ReasoningSliderProps) {
  const effective = value ?? defaultLevel;

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium text-raios-text">{label}</span>
        {effective && (
          <span className="text-xs text-raios-muted">
            {effective}
            {!supportedLevels.includes(effective) && (
              <span className="ml-1 text-raios-warning">→ fallback</span>
            )}
          </span>
        )}
      </div>
      <div className="flex gap-1" role="radiogroup" aria-label={label}>
        {ALL_LEVELS.map((level) => {
          const supported = supportedLevels.includes(level);
          const selected = effective === level;
          return (
            <button
              key={level}
              type="button"
              role="radio"
              aria-checked={selected}
              aria-disabled={!supported}
              title={
                supported
                  ? `Selecionar ${level}`
                  : `Modelo não suporta ${level} — adapter fará fallback automático pra nível inferior suportado`
              }
              onClick={() => onChange(level)}
              className={cn(
                'flex-1 px-2 py-1.5 text-xs rounded-md border transition-colors',
                selected
                  ? 'bg-raios-accent text-white border-raios-accent'
                  : supported
                  ? 'bg-raios-surface text-raios-text border-raios-border hover:bg-raios-surface-alt'
                  : 'bg-raios-surface/50 text-raios-muted/50 border-raios-border/50 cursor-not-allowed line-through',
              )}
            >
              {level}
            </button>
          );
        })}
      </div>
    </div>
  );
}
